import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  Grid,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";

import { FieldArray, Form, Formik, getIn } from "formik";
import * as Yup from "yup";
import { Close } from "@mui/icons-material";
import {
  useCreateVibeMutation,
} from "../../Slices/roomNateApis";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { LoadingButton } from "@mui/lab";
import { useGlobalAuthContext } from "../../context/AuthContext";
import axios from "axios";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  height: "90%",
  overflowY: "auto",
  width: { xs: "90%", md: "650px" },
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

export default function BasicModal({ FetchStyles }) {
  const formRef = React.useRef();
  const [createVibe, resultStats] = useCreateVibeMutation();
  const { currentUserSessionStorage } = useGlobalAuthContext();

  const [open, setOpen] = React.useState(false);
  const [load, setLoad] = React.useState(false);
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const validationSchema = Yup.object().shape({
    midJourneyPrompt: Yup.string().required("Mid Journey Prompt is required"),
    description: Yup.string().required("Description is required"),
    name: Yup.string().required("Name is required"),
    style: Yup.mixed().notRequired("Style is not required"),
    furnitureItems: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Name is required"),
        description: Yup.string().required("Description is required"),
        image: Yup.mixed().required("Image is required"),
        url: Yup.mixed().required("Url is required"),
        embedField: Yup.mixed().required("Embed Field is required"),
      })
    ),
    images: Yup.array().min(1, "Please upload at least one image"),
    moodboardImages: Yup.array().min(1, "Please upload at least one image"),
  });

  React.useEffect(() => {
    if (resultStats?.isLoading) {
      setLoad(true);
    }

    if (!resultStats?.isLoading) {
      setLoad(false);
    }

    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
      handleClose();
    }

    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [resultStats]);

  const handleImageDelete = (index) => {
    const updatedImages = [...formRef.current.values.images];
    updatedImages.splice(index, 1);
    formRef.current.setValues({
      ...formRef.current.values,
      images: updatedImages,
    });
  };

  const handleMoodboardImagesDelete = (index) => {
    const updatedImages = [...formRef.current.values.moodboardImages];
    updatedImages.splice(index, 1);
    formRef.current.setValues({
      ...formRef.current.values,
      moodboardImages: updatedImages,
    });
  };

  return (
    <>
      {successAlert && (
        <SuccessAlert title="Vibe has been created successfully!" />
      )}

      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      <Button onClick={handleOpen} variant="outlined" endIcon={<AddIcon />}>
        Add Vibe
      </Button>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box position="fixed" top={10} right={0}>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Formik
            innerRef={formRef}
            initialValues={{
              style: "",
              name: "",
              midJourneyPrompt: "",
              description: "",
              furnitureItems: [
                {
                  name: "",
                  description: "",
                  image: "",
                  url: "",
                  embedField: "",
                },
              ],
              images: [],
              moodboardImages: [],
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);

              const {
                style,
                images,
                midJourneyPrompt,
                furnitureItems,
                name,
                moodboardImages,
                description,
              } = values;
              let data = {
                style,
                images,
                midJourneyPrompt,
                furnitureItems,
                name,
                moodboardImages,
                description,
              };

              const uploadPromises = [];

              images.forEach((image, index) => {
                if (image.url !== "") {
                  const file = image.url;

                  if (file.size >= 1e7) {
                    return; 
                  }

                  const fd = new FormData();
                  fd.append("image", file);

                  const promise = axios
                    .post("/items/image-upload/", fd, {
                      headers: {
                        Authorization: `Bearer ${currentUserSessionStorage}`,
                      },
                    })
                    .then((resp) => {
                      const updatedImage = { ...image, url: resp.data.path }; 
                      const updatedImages = [...data.images];
                      updatedImages[index] = updatedImage;

                      data = {
                        ...data,
                        images: updatedImages,
                      };
                    })
                    .catch((error) => {
                      console.error("Image upload failed:", error);
                    });

                  uploadPromises.push(promise);
                }
              });

              moodboardImages.forEach((image, index) => {
                if (image.url !== "") {
                  const file = image.url;

                  if (file.size >= 1e7) {
                    return; 
                  }

                  const fd = new FormData();
                  fd.append("image", file);

                  const promise = axios
                    .post("/items/image-upload/", fd, {
                      headers: {
                        Authorization: `Bearer ${currentUserSessionStorage}`,
                      },
                    })
                    .then((resp) => {
                      const updatedImage = { ...image, url: resp.data.path }; 
                      const updatedImages = [...data.moodboardImages];
                      updatedImages[index] = updatedImage;

                      data = {
                        ...data,
                        moodboardImages: updatedImages,
                      };
                    })
                    .catch((error) => {
                      console.error("Image upload failed:", error);
                    });

                  uploadPromises.push(promise);
                }
              });

              furnitureItems.forEach((item, index) => {
                if (item.image !== "") {
                  const file = item.image;

                  if (file.size >= 1e7) {
                    return; 
                  }

                  const fd = new FormData();
                  fd.append("image", file);

                  const promise = axios
                    .post("/items/image-upload/", fd, {
                      headers: {
                        Authorization: `Bearer ${currentUserSessionStorage}`,
                      },
                    })
                    .then((resp) => {
                      const updatedItem = { ...item, image: resp.data.path }; 
                      const updatedItems = [...data.furnitureItems];
                      updatedItems[index] = updatedItem;

                      data = {
                        ...data,
                        furnitureItems: updatedItems,
                      };
                    })
                    .catch((error) => {
                      console.error("Image upload failed:", error);
                    });

                  uploadPromises.push(promise);
                }
              });

              Promise.all(uploadPromises)
                .then(() => {
                  createVibe(data);
                })
                .catch((error) => {
                  console.error("Image upload failed:", error);
                });
            }}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              setValues,
            }) => (
              <Form noValidate autoComplete="off">
                <Typography
                  id="modal-modal-title"
                  fontWeight={"bold"}
                  fontSize={"20px"}
                  npmsvariants="h6"
                  mb={2}
                >
                  Create a Vibe
                </Typography>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Name"
                    variants="outlined"
                    name="name"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    required
                    helperText={
                      getIn(touched, "name") && getIn(errors, "name")
                        ? getIn(errors, "name")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "name") && getIn(errors, "name")
                    )}
                  />
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Description"
                    variants="outlined"
                    name="description"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.description}
                    required
                    helperText={
                      getIn(touched, "description") &&
                      getIn(errors, "description")
                        ? getIn(errors, "description")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "description") &&
                        getIn(errors, "description")
                    )}
                  />
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Mid journey Promopt"
                    variants="outlined"
                    name="midJourneyPrompt"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.midJourneyPrompt}
                    required
                    helperText={
                      getIn(touched, "midJourneyPrompt") &&
                      getIn(errors, "midJourneyPrompt")
                        ? getIn(errors, "midJourneyPrompt")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "midJourneyPrompt") &&
                        getIn(errors, "midJourneyPrompt")
                    )}
                  />
                </Stack>

                {FetchStyles && (
                  <Autocomplete
                    fullWidth
                    disablePortal
                    onChange={(_, value) =>
                      setValues({
                        ...values,
                        style: value?._id,
                      })
                    }
                    value={FetchStyles?.find(
                      (item) => item._id === values?.style
                    )}
                    name={`parentStyle`}
                    id="combo-box-demo"
                    options={FetchStyles}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        placeholder={"Select Style"}
                        name={`style`}
                        {...params}
                        onBlur={handleBlur}
                        helperText={
                          getIn(touched, "style") && getIn(errors, "style")
                            ? getIn(errors, "style")
                            : ""
                        }
                        error={Boolean(
                          getIn(touched, "style") && getIn(errors, "style")
                        )}
                      />
                    )}
                  />
                )}

                <FieldArray name="furnitureItems">
                  {({ push, remove }) => (
                    <Box mt={2}>
                      {values?.furnitureItems?.map((p, index) => {
                        const name = `furnitureItems.[${index}].name`;
                        const touchedName = getIn(touched, name);
                        const errorName = getIn(errors, name);

                        const description = `furnitureItems.[${index}].description`;
                        const touchedDescription = getIn(touched, description);
                        const errorDescription = getIn(errors, description);

                        const image = `furnitureItems.[${index}].image`;
                        const touchedImage = getIn(touched, image);
                        const errorImage = getIn(errors, image);

                        const url = `furnitureItems.[${index}].url`;
                        const touchedUrl = getIn(touched, url);
                        const errorUrl = getIn(errors, url);

                        const embedField = `furnitureItems.[${index}].embedField`;
                        const touchedEmbedField = getIn(touched, embedField);
                        const errorEmbedField = getIn(errors, embedField);

                        return (
                          <Box key={index}>
                            <Typography
                              variant="h6"
                              fontWeight={"bold"}
                              fontSize={"20px"}
                            >
                              Furniture {index + 1}
                            </Typography>

                            <Grid container spacing={2} mb={2}>
                              <Grid item xs={10}>
                                <TextField
                                  variant="outlined"
                                  fullWidth
                                  label="Name"
                                  name={name}
                                  value={p.name}
                                  required
                                  helperText={
                                    touchedName && errorName ? errorName : ""
                                  }
                                  error={Boolean(touchedName && errorName)}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </Grid>

                              <Grid item xs={2}>
                                <Button
                                  type="button"
                                  variant="contained"
                                  onClick={() => remove(index)}
                                >
                                  x
                                </Button>
                              </Grid>

                              <Grid item xs={4} my={2}>
                                <Box display={"flex"} flexDirection={"column"}>
                                  <Box>
                                    <img
                                      src={
                                        p?.image
                                          ? URL.createObjectURL(p?.image)
                                          : "https://placehold.jp/90x90.png"
                                      }
                                      alt={p.url ? p.image.name : "placeHolder"}
                                      height={"100px"}
                                    />
                                  </Box>
                                  {touchedImage && errorImage && (
                                    <span className="error">
                                      {"Upload Image"}
                                    </span>
                                  )}

                                  <Button variant="contained" component="label">
                                    Upload
                                    <input
                                      hidden
                                      accept="image/*"
                                      type="file"
                                      name="topViewImage"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        const updatedImages = [
                                          ...values.furnitureItems,
                                        ];
                                        updatedImages[index].image = file;
                                        setValues({
                                          ...values,
                                          furnitureItems: updatedImages,
                                        });
                                      }}
                                    />
                                  </Button>
                                </Box>
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  variant="outlined"
                                  label="Description"
                                  name={description}
                                  fullWidth
                                  value={p.description}
                                  required
                                  helperText={
                                    touchedDescription && errorDescription
                                      ? errorDescription
                                      : ""
                                  }
                                  error={Boolean(
                                    touchedDescription && errorDescription
                                  )}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  variant="outlined"
                                  label="url"
                                  name={url}
                                  fullWidth
                                  value={p.url}
                                  required
                                  helperText={
                                    touchedUrl && errorUrl ? errorUrl : ""
                                  }
                                  error={Boolean(touchedUrl && errorUrl)}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  placeholder="Write embed code"
                                  multiline
                                  rows={7}
                                  fullWidth
                                  name={embedField}
                                  value={p.embedField}
                                  required
                                  helperText={
                                    touchedEmbedField && errorEmbedField
                                      ? errorEmbedField
                                      : ""
                                  }
                                  error={Boolean(
                                    touchedEmbedField && errorEmbedField
                                  )}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      })}
                      <div
                        className={`${
                          values?.furnitureItems?.length < 1 ? "set-div" : null
                        }`}
                      >
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={() =>
                            push({
                              name: "",
                              description: "",
                              image: "",
                              url: "",
                              embedField: "",
                            })
                          }
                        >
                          Add Furniture
                        </Button>
                      </div>
                    </Box>
                  )}
                </FieldArray>

                <Box mt={2}>
                  <Typography
                    variant="h6"
                    fontWeight={"bold"}
                    fontSize={"20px"}
                  >
                    Mood Board Images
                  </Typography>

                  <Stack my={2} display={"flex"} flexDirection={"column"}>
                    {values?.moodboardImages.length >= 1 ? (
                      values?.moodboardImages.map((p, idx) => {
                        return (
                          <Box
                            display={"flex"}
                            flexDirection={"row"}
                            alignItems={"center"}
                          >
                            <img
                              src={URL.createObjectURL(p?.url)}
                              alt={p.url.name}
                              height={"100px"}
                            />

                            <Button
                              style={{ marginLeft: "0px" }}
                              variant="contained"
                              color="secondary"
                              onClick={() => handleMoodboardImagesDelete(idx)}
                            >
                              x
                            </Button>
                          </Box>
                        );
                      })
                    ) : (
                      <img
                        src={"https://placehold.jp/90x90.png"}
                        alt={"placeHolder"}
                        height={"100px"}
                        width={100}
                      />
                    )}

                    {getIn(touched, "moodboardImages") &&
                      getIn(errors, "moodboardImages") && (
                        <span className="error">{errors.moodboardImages}</span>
                      )}
                  </Stack>

                  <Button variant="contained" component="label">
                    Upload
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      multiple
                      onChange={(e) => {
                        const { files } = e.target;
                        const updatedImages = [...values.moodboardImages];
                        for (let i = 0; i < files.length; i++) {
                          const file = files[i];
                          const imageObject = {
                            url: file,
                          };
                          updatedImages.push(imageObject);
                        }
                        setValues({
                          ...values,
                          moodboardImages: updatedImages,
                        });
                      }}
                    />
                  </Button>
                </Box>

                <Box mt={2}>
                  <Typography
                    variant="h6"
                    fontWeight={"bold"}
                    fontSize={"20px"}
                  >
                    Images
                  </Typography>

                  <Stack my={2} display={"flex"} flexDirection={"column"}>
                    {values?.images.length >= 1 ? (
                      values?.images.map((p, idx) => {
                        return (
                          <Box
                            display={"flex"}
                            flexDirection={"row"}
                            alignItems={"center"}
                          >
                            <img
                              src={URL.createObjectURL(p?.url)}
                              alt={p.url.name}
                              height={"100px"}
                            />

                            <TextField
                              style={{ margin: "0px 15px" }}
                              id="outlined-basic"
                              label="Name"
                              variants="outlined"
                              name={`p${[idx]}.name`}
                              fullWidth
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={p.name.split(".").slice(0, -1).join(".")}
                            />

                            <Button
                              style={{ marginLeft: "0px" }}
                              variant="contained"
                              color="secondary"
                              onClick={() => handleImageDelete(idx)}
                            >
                              x
                            </Button>
                          </Box>
                        );
                      })
                    ) : (
                      <img
                        src={"https://placehold.jp/90x90.png"}
                        alt={"placeHolder"}
                        height={"100px"}
                        width={100}
                      />
                    )}

                    {getIn(touched, "images") && getIn(errors, "images") && (
                      <span className="error">{errors.images}</span>
                    )}
                  </Stack>

                  <Button variant="contained" component="label">
                    Upload
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      multiple
                      onChange={(e) => {
                        const { files } = e.target;
                        const updatedImages = [...values.images];
                        for (let i = 0; i < files.length; i++) {
                          const file = files[i];
                          const imageObject = {
                            url: file,
                            name: file.name,
                          };

                          updatedImages.push(imageObject);
                        }
                        setValues({ ...values, images: updatedImages });
                      }}
                    />
                  </Button>
                </Box>

                <Stack mt={2}>
                  <LoadingButton
                    loading={load}
                    variant="contained"
                    fullWidth
                    type="submit"
                  >
                    Add Vibe
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
}
